import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Box,
    Button, CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Form, Formik, FormikProps} from 'formik';
import Grid from '@mui/material/Grid2';
import {
    PurchaseFormValues,
    PurchaseDialogProps
} from '../../../../entity/props/marketplace/purchase-dialog-props';
import {useService} from '../../../../infrastructure/service/service';
import {Project} from '../../../../entity/model/project';
import {Title} from '../../../../entity/model/title';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import {Section} from '../../../../entity/resource/section';
import {MarketplaceRecordRequestDto} from '../../../../entity/dto/marketplace-record-request-dto';
import {toast} from 'react-toastify';
import {MarketplaceType} from '../../../../entity/enums/marketplace-type';
import LoadingComponent from "../widget/LoadingComponent";

import '../../../../assets/css/style.css';
import '../../../../assets/css/marketplace/purchase-option-dialog.css';

const PurchaseDialog = (props: PurchaseDialogProps): JSX.Element => {
    const {projectService, titleService, userService, marketplaceService, auditLogsService} = useService();

    const [projects, setProjects] = useState<Project[]>([]);
    const [titles, setTitles] = useState<Title[]>([]);
    const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);
    const [titleOptions, setTitleOptions] = useState<DropdownOption[]>([]);
    const [sectionOptions, setSectionOptions] = useState<DropdownOption[]>([]);
    const [loadingImport, setLoadingImport] = useState<boolean>(false);
    const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [progressRefresh, setProgressRefresh] = useState<boolean>(false);
    const [batchId, setBatchId] = useState<string>('');
    const [progress, setProgress] = useState<number>(0);
    const [message, setMessage] = useState<string>('');
    const [lastStateMessage, setLastStateMessage] = useState<string>('');
    const [downloadUrl, setDownloadUrl] = useState<string>('');
    const [importProgressRefresh, setImportProgressRefresh] = useState<boolean>(false);
    const [importBatchId, setImportBatchId] = useState<string>('');
    const [importProgress, setImportProgress] = useState<number>(0);
    const [importMessage, setImportMessage] = useState<string>('');
    const [importLastStateMessage, setImportLastStateMessage] = useState<string>('');

    useEffect(() => {
        const init = async (): Promise<void> => {
            setLoading(true);

            const user = await userService.getCurrentUser();

            let projects = await projectService.getProjects(user?.organizationID ?? '', userService.isAdminUser());
            projects = projects.filter((project: Project): boolean => projectService.isPermittedProject(project.id));

            setProjects(projects);
            setProjectOptions(projects.map((project: Project): DropdownOption => ({key: project.id, text: project.orderNumber, value: project.id})));
            setTitles([]);
            setTitleOptions([]);
            setSectionOptions([])

            setLoading(false);
        }
        init();
    }, []);

    const handleValidate = (values: PurchaseFormValues): Record<string, string> => {
        const errors: Record<string, string> = {};

        if (!values.selectedFile)
            errors.selectedFile = 'Project is required.';

        if (!values.selectedTitle)
            errors.selectedTitle = 'Title is required.';

        if (!values.selectedSection)
            errors.selectedSection = 'Section is required.';

        if (!values.documentName)
            errors.documentName = 'Document name is required.';

        return errors;
    }

    const handleProjectChange = async (event: SelectChangeEvent, handleChange: any, setFieldValue: any): Promise<void> => {
        const selectedProject = event.target.value;
        handleChange(event);
        setLoading(true);

        setFieldValue('selectedTitle', '');
        setFieldValue('selectedSection', '');

        if (selectedProject) {
            const user = await userService.getCurrentUser();

            const projectTitles = await titleService.getTitles(user?.organizationID ?? '', selectedProject);
            projectTitles.filter((title: Title): boolean => projectService.isPermittedTitle(title.id));

            setTitles(projectTitles);
            setTitleOptions(projectTitles.map((title: Title) => ({
                key: title.id,
                text: title.tmsParcelNumbers[0],
                value: title.id
            })));
        } else {
            setTitles([]);
            setTitleOptions([]);
        }

        setLoading(false);
    }

    const handleTitleChange = (event: SelectChangeEvent, handleChange: any, setFieldValue: any): void => {
        const selectedTitle = event.target.value;
        handleChange(event);

        setFieldValue('selectedSection', '');

        if (selectedTitle) {
            const selectedTitleData = titles.find((title: Title) => title.id === selectedTitle);
            if (selectedTitleData) {
                setSectionOptions(selectedTitleData.sections.map((section: Section) => ({
                    key: section.id,
                    text: section.name,
                    value: section.id
                })));
            }
        } else
            setSectionOptions([]);
    }

    const handleDownload = async (): Promise<void> => {
        setLoadingDownload(true);
        setProgress(5);
        setMessage('Your download request is being processed');

        const user = await userService.getCurrentUser();
        const batchId = crypto.randomUUID();
        const payload: MarketplaceRecordRequestDto = {
            marketplaceDocumentMetadataID: props.documentMetadataID,
            requestMethod: MarketplaceType.DIRECT_DOWNLOAD,
            fromPage: parseInt(props.pageFrom) || 1,
            toPage: parseInt(props.pageTo) || 1,
            batchID: batchId
        };

        console.info(`[PurchaseDialog] Starting marketplace download with batchId: ${batchId}, organizationId: ${user?.organizationID}`, payload);
        marketplaceService.createMarketplaceRecord(user?.organizationID ?? '', payload);

        await fetchDownloadProgress(batchId, user?.organizationID ?? '');
    }

    const fetchDownloadProgress = async (batchId: string, organizationId: string): Promise<void> => {
        let previousProgress = 0;
        let progressUnchangedDuration = 0;
        const maxUnchangedDuration = 20000;
        const interval = 5000;
        const maxUnchangedCycles = maxUnchangedDuration / interval;

        const intervalId = setInterval(async (): Promise<void> => {
            const response = await auditLogsService.getMarketplaceDownloadLogs(organizationId, batchId);
            if (response.progress > progress)
                setProgress(response.progress);

            setMessage(response.message);

            if (response.progress === 90) {
                console.info(`[PurchaseDialog] Download progress reached 90% for batchId: ${batchId}`);
                setProgress(95);
                setMessage('Preparing PDF for download...');

                clearInterval(intervalId);

                console.info(`[PurchaseDialog] Calling downloadMarketplaceDocument with organizationId: ${organizationId}, batchId: ${batchId}`);
                const downloadUrl = await marketplaceService.downloadMarketplaceDocument(organizationId, batchId);

                if (downloadUrl) {
                    const filename = `marketplace-document-pages-${props.pageFrom}-${props.pageTo}.pdf`;
                    console.info(`[PurchaseDialog] Starting file download with filename: ${filename}`);
                    await marketplaceService.downloadFile(downloadUrl, filename);

                    setLoadingDownload(false);
                    setProgress(100);
                    props.tokenCallback();
                    setTimeout((): void => props.setOpen(false), 1000);
                } else {
                    console.error(`[PurchaseDialog] No download URL received for batchId: ${batchId}`);
                    setLoadingDownload(false);
                    setProgress(0);
                    setMessage('');
                    setTimeout((): void => props.setOpen(false), 1000);
                }
            }
            else if (response.progress === 30) {
                setLoadingDownload(false);
                setTimeout((): void => props.setOpen(false), 1000);
                clearInterval(intervalId);
            }

            if (response.progress !== previousProgress) {
                previousProgress = response.progress;
                progressUnchangedDuration = 0;
            } else
                progressUnchangedDuration++;

            if (progressUnchangedDuration >= maxUnchangedCycles) {
                setLoadingDownload(false);
                setProgressRefresh(true);
                setBatchId(batchId);
                setLastStateMessage(response.message);
                setMessage('Processing your download. Please refresh to check the progress.');
                marketplaceService.showTimeoutMessage();
                clearInterval(intervalId);
            }
        }, interval);
    }

    const handleProgressRefresh = async (): Promise<void> => {
        setProgressRefresh(false);
        setMessage(lastStateMessage);
        setProgress((prevState: number): number => prevState + 1);

        const user = await userService.getCurrentUser();
        await fetchDownloadProgress(batchId, user?.organizationID ?? '');
    }

    const handleImport = async (values: PurchaseFormValues): Promise<void> => {
        setLoadingImport(true);
        setImportProgress(5);
        setImportMessage('Your import request is being processed');

        const user = await userService.getCurrentUser();
        if (!user?.organizationID) {
            setLoadingImport(false);
            setImportProgress(0);
            setImportMessage('');
            return;
        }

        const selectedProject = projects.find((project: Project) => project.id === values.selectedFile);
        const selectedTitleData = titles.find((title: Title) => title.id === values.selectedTitle);
        if (!selectedProject || !selectedTitleData) {
            setLoadingImport(false);
            setImportProgress(0);
            setImportMessage('');
            return;
        }

        const batchId = crypto.randomUUID();
        const payload: MarketplaceRecordRequestDto = {
            marketplaceDocumentMetadataID: props.documentMetadataID,
            requestMethod: MarketplaceType.TITLE_IMPORT,
            fromPage: parseInt(props.pageFrom) || 1,
            toPage: parseInt(props.pageTo) || 1,
            titleID: values.selectedTitle,
            sectionID: values.selectedSection,
            documentName: values.documentName,
            batchID: batchId
        };

        marketplaceService.createMarketplaceRecord(selectedTitleData.assignedToOrganizationID, payload);

        await fetchImportProgress(batchId, selectedTitleData.assignedToOrganizationID);
    }

    const fetchImportProgress = async (batchId: string, organizationId: string): Promise<void> => {
        let previousProgress = 0;
        let progressUnchangedDuration = 0;
        const maxUnchangedDuration = 20000;
        const interval = 5000;
        const maxUnchangedCycles = maxUnchangedDuration / interval;

        const intervalId = setInterval(async (): Promise<void> => {
            const response = await auditLogsService.getMarketplaceImportLogs(organizationId, batchId);
            if (response.progress > importProgress)
                setImportProgress(response.progress);

            setImportMessage(response.message);

            if (response.progress === 90) {
                setLoadingImport(false);
                setImportProgress(100);
                setImportMessage('Document imported successfully!');

                props.tokenCallback();
                setTimeout((): void => props.setOpen(false), 1000);
                clearInterval(intervalId);
            }
            else if (response.progress === 30) {
                setLoadingImport(false);
                setTimeout((): void => props.setOpen(false), 1000);
                clearInterval(intervalId);
            }

            if (response.progress !== previousProgress) {
                previousProgress = response.progress;
                progressUnchangedDuration = 0;
            } else
                progressUnchangedDuration++;

            if (progressUnchangedDuration >= maxUnchangedCycles) {
                setLoadingImport(false);
                setImportProgressRefresh(true);
                setImportBatchId(batchId);
                setImportLastStateMessage(response.message);
                setImportMessage('Processing your import. Please refresh to check the progress.');
                marketplaceService.showImportTimeoutMessage();
                clearInterval(intervalId);
            }
        }, interval);
    }

    const handleImportProgressRefresh = async (): Promise<void> => {
        setImportProgressRefresh(false);
        setImportMessage(importLastStateMessage);
        setImportProgress((prevState: number): number => prevState + 1);

        const user = await userService.getCurrentUser();
        await fetchImportProgress(importBatchId, user?.organizationID ?? '');
    }

    const handleClose = (): void => {
        props.setOpen(false);
    }

    return (
        <Dialog open={props.open} className={'zitles-dialog purchase-options-dialog'}>
            <DialogTitle className={'zitles-dialog-title'}>
                <span>Purchase Options</span>
                <IconButton onClick={handleClose} disabled={progress > 0 || importProgress > 0}>
                    <CloseIcon className={'dialog-icon'}/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers className={'zitles-dialog-content'}>
                <Box>
                    <Box className={'purchase-options-download-section'}>
                        <Typography variant='h6' component='h4' className={'purchase-options-download-title'}>Download to PC</Typography>
                        <Typography variant='body2' color='text.secondary' className={'purchase-options-download-description'}>
                            You can Directly Download the Document to your PC
                        </Typography>
                    </Box>
                    <Box className={'purchase-options-download-section'}>
                        {
                            progress > 0
                                ? <Box className={'progress-zone'}>
                                    <Box className={'progress-icon-zone'}>
                                        {
                                            progressRefresh
                                                ? <RefreshIcon className={'progress-icon'} cursor={'pointer'} onClick={handleProgressRefresh}/>
                                                : <InsertDriveFileIcon className={'progress-icon'} />
                                        }
                                    </Box>
                                    <Box className={'progress-bar-zone'}>
                                        <Typography className={'progress-bar-title'}>{message}</Typography>
                                        <Box className={'progress-bar-container'}>
                                            <Box className={'progress-bar'}>
                                                <LinearProgress variant='determinate' value={progress} color='success' className={'progress'}/>
                                            </Box>
                                            <Typography className={'progress-value'}>{`${progress}%`}</Typography>
                                        </Box>
                                    </Box>
                                  </Box>
                                : <Button
                                    variant='contained'
                                    fullWidth
                                    startIcon={loadingDownload ? <CircularProgress size={14} color='inherit' /> : <DownloadIcon/>}
                                    onClick={handleDownload}
                                    disabled={loadingDownload || loadingImport}
                                    className={`zitles-button submit purchase-options-download-button ${loadingImport || loadingDownload ? 'disable' : ''}`}>
                                    {
                                        loadingDownload ? 'Processing' : 'Download File'
                                    }
                                  </Button>
                        }
                    </Box>
                    <Divider className={'purchase-options-divider'}>
                        <Typography variant='body2' color='text.secondary' className={'purchase-options-divider-text'}>or</Typography>
                    </Divider>
                    <Box className={'purchase-options-import-section'}>
                        <Typography variant='h6' component='h4' className={'purchase-options-import-title'}>Import to Zitles Order</Typography>
                        <Formik
                            initialValues={{selectedFile: '', selectedTitle: '', selectedSection: '', documentName: ''}}
                            validate={handleValidate}
                            onSubmit={handleImport}>
                            {({values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue}: FormikProps<PurchaseFormValues>): JSX.Element => (
                                <Form onSubmit={handleSubmit}>
                                    <Grid container spacing={3} className={'purchase-options-form-container'}>
                                        <Grid size={12} className={'purchase-options-form-grid'}>
                                            <FormControl required fullWidth error={touched.selectedFile && Boolean(errors.selectedFile)}>
                                                <InputLabel>Select Project</InputLabel>
                                                <Select
                                                    name='selectedFile'
                                                    value={values.selectedFile}
                                                    label='Select Project'
                                                    onBlur={handleBlur}
                                                    onChange={(event: SelectChangeEvent) => handleProjectChange(event, handleChange, setFieldValue)}>
                                                    {
                                                        projectOptions.map((project) =>
                                                            <MenuItem key={project.key} value={project.value}>
                                                                {project.text}
                                                            </MenuItem>
                                                        )
                                                    }
                                                </Select>
                                                <FormHelperText>{touched.selectedFile && errors.selectedFile}</FormHelperText>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={12} className={'purchase-options-form-grid'}>
                                            <FormControl required fullWidth error={touched.selectedTitle && Boolean(errors.selectedTitle)}>
                                                <InputLabel>Select Title</InputLabel>
                                                <Select
                                                    name='selectedTitle'
                                                    value={values.selectedTitle}
                                                    label='Select Title'
                                                    onBlur={handleBlur}
                                                    onChange={(event: SelectChangeEvent) => handleTitleChange(event, handleChange, setFieldValue)}
                                                    disabled={!values.selectedFile}>
                                                    {
                                                        titleOptions.map((title) =>
                                                            <MenuItem key={title.key} value={title.value}>
                                                                {title.text}
                                                            </MenuItem>
                                                        )
                                                    }
                                                </Select>
                                                <FormHelperText>{touched.selectedTitle && errors.selectedTitle}</FormHelperText>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={12} className={'purchase-options-form-grid'}>
                                            <FormControl required fullWidth error={touched.selectedSection && Boolean(errors.selectedSection)}>
                                                <InputLabel>Select Section</InputLabel>
                                                <Select
                                                    name='selectedSection'
                                                    value={values.selectedSection}
                                                    label='Select Section'
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    disabled={!values.selectedFile || !values.selectedTitle}>
                                                    {
                                                        sectionOptions.map((section) =>
                                                            <MenuItem key={section.key} value={section.value}>
                                                                {section.text}
                                                            </MenuItem>
                                                        )
                                                    }
                                                </Select>
                                                <FormHelperText>{touched.selectedSection && errors.selectedSection}</FormHelperText>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={12} className={'purchase-options-form-grid'}>
                                            <TextField
                                                required
                                                fullWidth
                                                label='Document Name'
                                                name='documentName'
                                                value={values.documentName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.documentName && Boolean(errors.documentName)}
                                                helperText={touched.documentName && errors.documentName}
                                                placeholder='Enter document name'/>
                                        </Grid>
                                    </Grid>
                                    <Divider className={'purchase-options-form-divider'}/>
                                    {
                                        importProgress > 0
                                            ? <Box className={'progress-zone'}>
                                                <Box className={'progress-icon-zone'}>
                                                    {
                                                        importProgressRefresh
                                                            ? <RefreshIcon className={'progress-icon'} cursor={'pointer'} onClick={handleImportProgressRefresh}/>
                                                            : <InsertDriveFileIcon className={'progress-icon'} />
                                                    }
                                                </Box>
                                                <Box className={'progress-bar-zone'}>
                                                    <Typography className={'progress-bar-title'}>{importMessage}</Typography>
                                                    <Box className={'progress-bar-container'}>
                                                        <Box className={'progress-bar'}>
                                                            <LinearProgress variant='determinate' value={importProgress} color='success' className={'progress'}/>
                                                        </Box>
                                                        <Typography className={'progress-value'}>{`${importProgress}%`}</Typography>
                                                    </Box>
                                                </Box>
                                              </Box>
                                            : <Box className={'purchase-options-actions'}>
                                                <Button
                                                    onClick={handleClose}
                                                    disabled={progress > 0 || importProgress > 0}
                                                    className={`zitles-button cancel ${progress > 0 || importProgress > 0 ? 'disable' : ''}`}>
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type='submit'
                                                    startIcon={loadingImport ? <CircularProgress size={14} color='inherit' /> : <FileUploadIcon/>}
                                                    disabled={loadingImport || loadingDownload || progress > 0 || importProgress > 0}
                                                    className={`zitles-button submit ${loadingDownload || loadingImport || progress > 0 || importProgress > 0 ? 'disable' : ''}`}>
                                                    {
                                                        loadingImport ? 'Processing' : 'Import to Order'
                                                    }
                                                </Button>
                                              </Box>
                                    }
                                </Form>
                            )}
                        </Formik>
                    </Box>
                </Box>
            </DialogContent>
            {
                loading &&
                    <LoadingComponent/>
            }
        </Dialog>
    );
}

export default observer(PurchaseDialog);
